import React, { useEffect, useState, useMemo } from "react"
import Modal from "../../components/Modal/Modal"
import GearSlot from "./GearSlot"
import Settings from "./Settings"
import classes from "./SeedRecover.module.css"
import Controls from "./Controls"
import ResultSet from "./ResultSet"
import GearDatabaseList from "../GearDatabaseList/GearDatabaseList"
import RefineMax from "./RefineMax"
import MurmurHash3 from "murmurhash3js"
import { next } from "../SeedChecker/SeedCalculator"
import { useLocalStorage, valid_key, decrypted_key, brand_names, SkillIDs, SkillIDsRev, ability_order, generate_brands } from "../../Utils"
import { defaultGearList } from "../../DefaultEntries"
import { useTranslation } from "react-i18next"

const typeList = ["Head", "Clothes", "Shoes"]

const SeedRecover = props => {
    const { t } = useTranslation()

    const [loading, setLoading] = useState(false)
    const brands = generate_brands(t, true)
    const [currentBrand, setCurrentBrand] = useState(brands[0])
    const [currentAbilities, setCurrentAbilities] = useState([
        [
            { value: "None", label: t("misc:Common.None") },
            { value: "None", label: t("misc:Common.None") },
        ],
    ])
    const [displayResults, setDisplayResults] = useState([])
    const [currentGear, setCurrentGear] = useState({ Label: t("misc:Common.None") })
    const [refine_max, setRefineMax] = useState(10000)

    const [gearList, setGearList] = useLocalStorage("gearList", defaultGearList)
    const worker = useMemo(() => new Worker("./seed-worker.js"), [])

    useEffect(() => {
        worker.onmessage = e => {
            if (e && e.data) {
                setDisplayResults(e.data.res)
                setLoading(false)
            }
        }
    }, [worker])

    const brandChangeHandler = event => {
        setCurrentBrand(event)
    }

    const changeAbilityHandler = (event, idx) => {
        setCurrentAbilities(current => {
            const abilities = [...current]
            abilities[idx][0] = event
            return abilities
        })
    }

    const changeDrinkHandler = (event, idx) => {
        setCurrentAbilities(current => {
            const abilities = [...current]
            abilities[idx][1] = event
            return abilities
        })
    }

    const startSearchHandler = event => {
        const sequence = []
        const drinks = []
        currentAbilities.forEach(elem => {
            if (elem[0].value === "None") {
                sequence.push(0xffffffff)
            } else {
                sequence.push(ability_order.indexOf(elem[0].value))
            }
            if (elem[1].value === "None") {
                drinks.push(0xffffffff)
            } else {
                drinks.push(ability_order.indexOf(elem[1].value))
            }
        })
        const brand = brand_names.indexOf(currentBrand.value)
        setLoading(true)
        worker.postMessage({ sequence, drinks, brand, refine_max })
    }

    const gearChangeHandler = newGear => {
        setCurrentBrand({
            value: newGear.value.Brand,
            label: t("translation:CommonMsg.Gear.GearBrandName." + newGear.value.Brand),
        })
        setCurrentGear(newGear)
        const abilities = []
        for (let i = 0; i < newGear.OriginalEntry.ExSkillArray.length || i < newGear.OriginalEntry.ExDrinksArray.length; i++) {
            abilities.push([
                {
                    value:
                        newGear.OriginalEntry.ExSkillArray[i] === 255 ||
                        newGear.OriginalEntry.ExSkillArray[i] === -1 ||
                        newGear.OriginalEntry.ExSkillArray[i] === undefined
                            ? "None"
                            : SkillIDsRev[newGear.OriginalEntry.ExSkillArray[i]],
                    label:
                        newGear.OriginalEntry.ExSkillArray[i] === 255 ||
                        newGear.OriginalEntry.ExSkillArray[i] === -1 ||
                        newGear.OriginalEntry.ExSkillArray[i] === undefined
                            ? t("misc:Common.None")
                            : t("translation:CommonMsg.Gear.GearPowerName." + SkillIDsRev[newGear.OriginalEntry.ExSkillArray[i]]),
                },
                {
                    value:
                        newGear.OriginalEntry.ExDrinksArray[i] === 255 || newGear.OriginalEntry.ExDrinksArray[i] === undefined
                            ? "None"
                            : SkillIDsRev[newGear.OriginalEntry.ExDrinksArray[i]],
                    label:
                        newGear.OriginalEntry.ExDrinksArray[i] === 255 || newGear.OriginalEntry.ExDrinksArray[i] === undefined
                            ? t("misc:Common.None")
                            : t("translation:CommonMsg.Lobby.LobbyFoodName." + SkillIDsRev[newGear.OriginalEntry.ExDrinksArray[i]]),
                },
            ])
        }

        abilities.push([
            { value: "None", label: t("misc:Common.None") },
            { value: "None", label: t("misc:Common.None") },
        ])
        setCurrentAbilities(abilities)

        if (valid_key()) {
            setRefineMax(1000)
        }
    }

    const filterFunction = val => {
        return val.RandomContext === 0
    }

    const refineSearchHandler = val => {
        const sequence = []
        const drinks = []
        currentAbilities.forEach(elem => {
            if (elem[0].value === "None") {
                sequence.push(0xffffffff)
            } else {
                sequence.push(ability_order.indexOf(elem[0].value))
            }
            if (elem[1].value === "None") {
                drinks.push(0xffffffff)
            } else {
                drinks.push(ability_order.indexOf(elem[1].value))
            }
        })
        const brand = brand_names.indexOf(currentBrand.value)
        setLoading(true)
        const displaySequence = displayResults
        worker.postMessage({ sequence, drinks, brand, displaySequence, refine_max })
    }

    const updateGearHandler = val => {
        const abilities = new Array(currentAbilities.length)
        const drinks = new Array(currentAbilities.length)
        for (let i = 0; i < currentAbilities.length; i++) {
            const [ability, drink] = currentAbilities[i]
            if (ability.value !== "None") {
                abilities[i] = SkillIDs[ability.value]
            } else {
                abilities[i] = 255
            }
            if (drink.value !== "None") {
                drinks[i] = SkillIDs[drink.value]
            } else {
                drinks[i] = 255
            }
        }
        while (abilities[abilities.length - 1] === 255 && drinks[drinks.length - 1] === 255) {
            abilities.splice(abilities.length - 1, 1)
            drinks.splice(drinks.length - 1, 1)
        }

        const original = { ...currentGear.OriginalEntry }
        if (displayResults.length === 1) {
            original.RandomContext = parseInt(displayResults[0].Seed.substring(2), 16)
        }
        original.ExSkillArray = abilities
        original.ExDrinksArray = drinks
        const gearKey = currentGear.Type === 0 ? "HaveGearHeadMap" : currentGear.Type === 1 ? "HaveGearClothesMap" : "HaveGearShoesMap"

        const new_dict = { ...gearList }
        new_dict[gearKey][currentGear.Id] = original
        setGearList(new_dict)
    }

    const checkNumberOfRefineCountHandler = event => {
        let inputstr = event.target.value
        inputstr = inputstr.replace(/[^0-9]/g, "")
        while (inputstr.length > 0 && inputstr.substring(0, 1) === "0") {
            inputstr = inputstr.slice(1)
        }
        if (inputstr.length === 0) {
            inputstr = "0"
        }
        inputstr = inputstr.substring(0, 9) // cut off too many characters
        setRefineMax(Math.min(parseInt(inputstr), 2500000))
    }

    const startSearchHandlerFast = () => {
        const gearKey = typeList[currentGear.Type]
        const start_seed =
            currentGear.Type === 1 && currentGear.Id === 26000
                ? MurmurHash3.x86.hash32(`${decrypted_key()}_${gearKey}_${currentGear.Id}_JUEA-00100`) >>> 0
                : MurmurHash3.x86.hash32(`${decrypted_key()}_${gearKey}_${currentGear.Id}`) >>> 0

        const sequence = []
        const drinks = []
        const displaySequence = []
        let current_seed = start_seed
        for (let i = 0; i < refine_max; i++) {
            displaySequence.push({ Start: current_seed })
            current_seed = next(current_seed) >>> 0
        }

        currentAbilities.forEach(elem => {
            if (elem[0].value === "None") {
                sequence.push(0xffffffff)
            } else {
                sequence.push(ability_order.indexOf(elem[0].value))
            }
            if (elem[1].value === "None") {
                drinks.push(0xffffffff)
            } else {
                drinks.push(ability_order.indexOf(elem[1].value))
            }
        })
        const brand = brand_names.indexOf(currentBrand.value)
        setLoading(true)
        worker.postMessage({ sequence, drinks, brand, displaySequence, refine_max })
    }

    const addEntry = () => {
        const new_arr = [...currentAbilities]
        new_arr.push([
            { value: "None", label: t("misc:Common.None") },
            { value: "None", label: t("misc:Common.None") },
        ])
        setCurrentAbilities(new_arr)
    }

    const removeEntry = () => {
        const new_arr = [...currentAbilities]
        new_arr.pop()
        setCurrentAbilities(new_arr)
    }

    return (
        <>
            <Modal show={loading} />
            <h1>Splatoon 3 - {t("misc:Navigation.GearSeedRecovery")}</h1>
            <GearDatabaseList currentGear={currentGear} gearChange={gearChangeHandler} filterFunction={filterFunction} />
            <Settings currentBrand={currentBrand} brandChange={brandChangeHandler} />
            <div className={classes.WrapperContent}>
                <div className={classes.ButtonArea}>
                    <button className={classes.Button} disabled={currentAbilities.length === 1} onClick={removeEntry}>
                        -
                    </button>
                    <span style={{ marginRight: 10 }}>{t("misc:Common.Slot")}</span>
                    <button className={classes.Button} onClick={addEntry}>
                        +
                    </button>
                </div>
                <div className={classes.break}></div>
                <div className={classes.FlexWrapper}>
                    {currentAbilities.map(([slot, drink], idx) => (
                        <GearSlot
                            key={idx}
                            idx={idx}
                            currentSlot={slot}
                            changeSlot={changeAbilityHandler}
                            currentDrink={drink}
                            changeDrink={changeDrinkHandler}
                        />
                    ))}
                </div>
                <div className={classes.break}></div>
                <div className={classes.ButtonArea}>
                    <button className={classes.Button} disabled={currentAbilities.length === 1} onClick={removeEntry}>
                        -
                    </button>
                    <span style={{ marginRight: 10 }}>{t("misc:Common.Slot")}</span>
                    <button className={classes.Button} onClick={addEntry}>
                        +
                    </button>
                </div>
            </div>
            <RefineMax refineHandler={checkNumberOfRefineCountHandler} value={refine_max} />
            <Controls
                startSearch={startSearchHandler}
                startSearchFast={startSearchHandlerFast}
                refineSearch={refineSearchHandler}
                refineDisabled={displayResults.length >= refine_max || displayResults.length < 1}
                gearDisabled={currentGear.Label === "None"}
                updateGear={updateGearHandler}
                haskey={valid_key()}
            />
            <ResultSet results={displayResults} />
        </>
    )
}

export default SeedRecover
